/**
 * Demo Data Service
 * Provides placeholder/demo data for Trial mode - simulates a successful flex space
 * that has been running for the last 6 months
 */

import { getPlaceholderAvatarUrl } from '../utils/avatar.js'
import { getPlaceholderResourceImageUrl } from '../utils/resourceImage.js'

const DEMO_LOCATION = {
  location_id: 'demo-location-1',
  name: 'Innovation Hub',
  timezone: 'America/New_York',
  timezone_name: 'Eastern Time',
  __typename: 'Location'
}

// Real names for schedule items (bookings, tours, assignments)
const SCHEDULE_ACCOUNTS = [
  { account_id: '1', name: 'Alex Chen', email: 'alex@acme.co' },
  { account_id: '2', name: 'Jordan Taylor', email: 'jordan@startup.io' },
  { account_id: '3', name: 'Quinn Wilson', email: 'quinn@design.co' },
  { account_id: '4', name: 'Sam Park', email: 'sam@tech.com' },
  { account_id: '5', name: 'Riley Brown', email: 'riley@consulting.com' },
  { account_id: '6', name: 'Casey Rivera', email: 'casey@example.com' },
  { account_id: '7', name: 'Morgan Kim', email: 'morgan@example.com' },
  { account_id: '8', name: 'Avery Lee', email: 'avery@example.com' },
  { account_id: '9', name: 'Taylor Patel', email: 'taylor@example.com' },
  { account_id: '10', name: 'Jamie O\'Brien', email: 'jamie@example.com' },
  { account_id: '11', name: 'Skyler Brooks', email: 'skyler@example.com' },
  { account_id: '12', name: 'Parker Reed', email: 'parker@example.com' },
  { account_id: '13', name: 'Cameron Hayes', email: 'cameron@example.com' },
  { account_id: '14', name: 'Reese Morgan', email: 'reese@example.com' },
  { account_id: '15', name: 'Finley Quinn', email: 'finley@example.com' }
]

/**
 * Generates demo schedule data (bookings and tours)
 * @param {Object} options - Options for generating data
 * @param {number} options.from_timestamp - Start timestamp
 * @param {number} options.to_timestamp - End timestamp
 * @returns {Object} Demo schedule data matching API structure
 */
export function getDemoScheduleData(options = {}) {
  const { from_timestamp, to_timestamp } = options
  const now = Date.now() / 1000 // Current time in seconds
  
  // Generate bookings
  const bookings = []
  const tours = []
  const assignments = []
  
  // Successful flex space: 60-80 bookings for Top Bookers
  // Top Bookers fetches 180 days but filters to selected range (e.g. last 30 days) client-side.
  // Bias 75% of bookings toward the most recent 30 days so they appear in typical "last 30 days" view.
  const fromTime = from_timestamp ?? (now - 7 * 24 * 3600)
  const toTime = to_timestamp ?? (now + 14 * 24 * 3600)
  const timeRange = toTime - fromTime
  const recentWindow = Math.min(30 * 24 * 3600, timeRange * 0.25) // Last 30 days or 25% of range
  const recentStart = toTime - recentWindow
  const bookingCount = 60 + Math.floor(Math.random() * 21)
  
  // Weighted distribution: top 3 users get 15-20 each, next 5 get 6-10, rest get 2-4
  const userBookingCounts = []
  const topCount = 15 + Math.floor(Math.random() * 6)
  const midCount = 6 + Math.floor(Math.random() * 5)
  const lowCount = 2 + Math.floor(Math.random() * 3)
  for (let u = 0; u < SCHEDULE_ACCOUNTS.length; u++) {
    if (u < 3) userBookingCounts.push(topCount)
    else if (u < 8) userBookingCounts.push(midCount)
    else userBookingCounts.push(lowCount)
  }
  let bookingIndex = 0
  for (let userIdx = 0; userIdx < SCHEDULE_ACCOUNTS.length && bookingIndex < bookingCount; userIdx++) {
    const count = Math.min(userBookingCounts[userIdx] || lowCount, bookingCount - bookingIndex)
    for (let j = 0; j < count; j++) {
      // 75% in recent window (last 30 days), 25% spread across full range
      const inRecent = Math.random() < 0.75
      const startTime = inRecent
        ? recentStart + Math.random() * recentWindow
        : fromTime + Math.random() * timeRange
      const endTime = startTime + 3600 // 1 hour duration
      const acc = SCHEDULE_ACCOUNTS[userIdx]
      bookings.push({
        type: 'booking',
        booking_id: `demo-booking-${bookingIndex + 1}`,
        assignment_id: null,
        availability_block_id: null,
        is_recurring: false,
        title: `Meeting Room ${(bookingIndex % 10) + 1}`,
        resource: {
          resource_id: `resource-${(bookingIndex % 10) + 1}`,
          name: `Workspace ${String.fromCharCode(65 + (bookingIndex % 10))}`,
          type: {
            name: 'Meeting Room',
            __typename: 'ResourceType'
          },
          main_image: {
            url: getPlaceholderResourceImageUrl(`Workspace ${String.fromCharCode(65 + (bookingIndex % 10))}`),
            __typename: 'Image'
          },
          location: DEMO_LOCATION,
          parent_resources: [],
          child_resources: [],
          __typename: 'Resource'
        },
        location: DEMO_LOCATION,
        status: 'confirmed',
        start_timestamp: Math.floor(startTime),
        end_timestamp: Math.floor(endTime),
        time_intervals: [{
          start_timestamp: Math.floor(startTime),
          end_timestamp: Math.floor(endTime),
          __typename: 'TimeInterval'
        }],
        owner_account: {
          account_id: acc.account_id,
          name: acc.name,
          type: 'user',
          billing_email: acc.email,
          avatar: {
            url: getPlaceholderAvatarUrl(acc.name),
            __typename: 'Image'
          },
          __typename: 'Account'
        },
        tour_id: null,
        __typename: 'ScheduleItem'
      })
      bookingIndex++
    }
  }
  
  // Successful flex space: 5-8 tours today
  const tourCount = 5 + Math.floor(Math.random() * 4)
  for (let i = 0; i < tourCount; i++) {
    const startTime = now + (i * 3600 * 4) + 1800 // Every 4 hours, offset by 30 min
    const endTime = startTime + 1800 // 30 minute duration
    
    tours.push({
      type: 'tour',
      booking_id: null,
      assignment_id: null,
      availability_block_id: null,
      is_recurring: false,
      title: `Property Tour ${i + 1}`,
      resource: {
        resource_id: `tour-resource-${i + 1}`,
        name: `Tour Space ${i + 1}`,
        type: {
          name: 'Tour',
          __typename: 'ResourceType'
        },
        main_image: {
          url: getPlaceholderResourceImageUrl(`Tour Space ${i + 1}`),
          __typename: 'Image'
        },
        location: DEMO_LOCATION,
        parent_resources: [],
        child_resources: [],
        __typename: 'Resource'
      },
      location: DEMO_LOCATION,
      status: 'scheduled',
      start_timestamp: Math.floor(startTime),
      end_timestamp: Math.floor(endTime),
      time_intervals: [{
        start_timestamp: Math.floor(startTime),
        end_timestamp: Math.floor(endTime),
        __typename: 'TimeInterval'
      }],
      owner_account: (() => {
        const acc = SCHEDULE_ACCOUNTS[i % SCHEDULE_ACCOUNTS.length]
        return {
          account_id: acc.account_id,
          name: acc.name,
          type: 'staff',
          billing_email: acc.email,
          avatar: {
            url: getPlaceholderAvatarUrl(acc.name),
            __typename: 'Image'
          },
          __typename: 'Account'
        }
      })(),
      tour_id: `demo-tour-${i + 1}`,
      __typename: 'ScheduleItem'
    })
  }
  
  // Office/studio names for assignments (cycled through)
  const ASSIGNMENT_NAMES = [
    'Private Office A', 'Private Office B', 'Private Office C',
    'Studio 1', 'Studio 2', 'Studio 3',
    'Dedicated Desk North', 'Dedicated Desk South',
    'Meeting Room East', 'Meeting Room West',
    'Conference Room Alpha', 'Conference Room Beta',
    'Creative Studio', 'Focus Room', 'Quiet Office'
  ]

  // Successful flex space: 12-18 active assignments
  const assignmentCount = 12 + Math.floor(Math.random() * 7)
  for (let i = 0; i < assignmentCount; i++) {
    // Assignments ending within the time range (end_timestamp between from_timestamp and to_timestamp)
    const fromTime = from_timestamp || now
    const toTime = to_timestamp || (now + 7 * 24 * 3600) // Default to 7 days if not provided
    const timeRange = toTime - fromTime
    const endTime = fromTime + (Math.random() * timeRange) // Random end time within range
    const startTime = endTime - (3600 * 24 * (2 + Math.random() * 5)) // Start 2-7 days before end

    const assignmentName = ASSIGNMENT_NAMES[i % ASSIGNMENT_NAMES.length]

    assignments.push({
      type: 'assignment',
      booking_id: null,
      assignment_id: `demo-assignment-${i + 1}`,
      availability_block_id: null,
      is_recurring: false,
      title: assignmentName,
      resource: {
        resource_id: `assignment-resource-${i + 1}`,
        name: assignmentName,
        type: {
          name: 'Desk',
          __typename: 'ResourceType'
        },
        main_image: {
          url: getPlaceholderResourceImageUrl(assignmentName),
          __typename: 'Image'
        },
        location: DEMO_LOCATION,
        parent_resources: [],
        child_resources: [],
        __typename: 'Resource'
      },
      location: DEMO_LOCATION,
      status: 'active',
      start_timestamp: Math.floor(startTime),
      end_timestamp: Math.floor(endTime),
      time_intervals: [{
        start_timestamp: Math.floor(startTime),
        end_timestamp: Math.floor(endTime),
        __typename: 'TimeInterval'
      }],
      owner_account: (() => {
        const acc = SCHEDULE_ACCOUNTS[i % SCHEDULE_ACCOUNTS.length]
        return {
          account_id: acc.account_id,
          name: acc.name,
          type: 'user',
          billing_email: acc.email,
          avatar: {
            url: getPlaceholderAvatarUrl(acc.name),
            __typename: 'Image'
          },
          __typename: 'Account'
        }
      })(),
      tour_id: null,
      __typename: 'ScheduleItem'
    })
  }
  
  // Combine and sort by start time
  const allItems = [...bookings, ...tours, ...assignments].sort((a, b) => a.start_timestamp - b.start_timestamp)
  
  return {
    schedule: {
      total: allItems.length,
      data: allItems,
      __typename: 'Schedule'
    }
  }
}

/**
 * Demo data for Upcoming Bookings widget (schedule type: BOOKING)
 * @param {Object} options - { from_timestamp, to_timestamp }
 * @returns {Object} Demo schedule data with bookings only
 */
export function getDemoUpcomingBookingsData(options = {}) {
  const now = Math.floor(Date.now() / 1000)
  const { to_timestamp = now + 7 * 86400 } = options

  const resourceNames = [
    'Meeting Room A', 'Meeting Room B', 'Conference Room',
    'Phone Booth 1', 'Podcast Studio', 'Board Room',
    'Workshop Space', 'Hot Desk 3'
  ]

  const bookings = []
  // Fixed count based on period: current (future) gets 14, previous (past) gets 11 → positive comparison
  const isFuturePeriod = to_timestamp > now
  const count = isFuturePeriod ? 14 : 11
  for (let i = 0; i < count; i++) {
    const spread = isFuturePeriod ? (to_timestamp - now) : (to_timestamp - (options.from_timestamp || now - 7 * 86400))
    const base = isFuturePeriod ? now + 3600 : (options.from_timestamp || now - 7 * 86400)
    const startTime = base + ((i + 1) / (count + 1)) * spread
    const acc = SCHEDULE_ACCOUNTS[i % SCHEDULE_ACCOUNTS.length]
    const resName = resourceNames[i % resourceNames.length]
    bookings.push({
      booking_id: `demo-ub-${i + 1}`,
      title: resName,
      start_timestamp: Math.floor(startTime),
      end_timestamp: Math.floor(startTime + 3600),
      resource: {
        resource_id: `res-ub-${i + 1}`,
        name: resName,
        main_image: { url: getPlaceholderResourceImageUrl(resName), __typename: 'Image' },
        location: DEMO_LOCATION,
        __typename: 'Resource'
      },
      location: DEMO_LOCATION,
      owner_account: {
        account_id: acc.account_id,
        name: acc.name,
        avatar: { url: getPlaceholderAvatarUrl(acc.name), __typename: 'Image' },
        __typename: 'Account'
      },
      __typename: 'ScheduleItem'
    })
  }

  bookings.sort((a, b) => a.start_timestamp - b.start_timestamp)

  return {
    schedule: {
      total: bookings.length,
      data: bookings,
      __typename: 'Schedule'
    }
  }
}

/**
 * Demo data for Assignments Starting Soon widget (schedule type: ASSIGNMENT)
 * @param {Object} options - { from_timestamp, to_timestamp }
 * @returns {Object} Demo schedule data with assignments starting in range
 */
export function getDemoAssignmentsStartingSoonData(options = {}) {
  const now = Math.floor(Date.now() / 1000)
  const { from_timestamp = now, to_timestamp = now + 7 * 86400 } = options

  const names = [
    'Private Office A', 'Studio 1', 'Dedicated Desk North',
    'Creative Studio', 'Private Office B', 'Focus Room'
  ]

  const assignments = []
  // Fixed count based on period: current (future) gets 6, previous (past) gets 4 → positive comparison
  const isFuturePeriod = to_timestamp > now
  const count = isFuturePeriod ? 6 : 4
  for (let i = 0; i < count; i++) {
    const startTime = from_timestamp + ((i + 1) / (count + 1)) * (to_timestamp - from_timestamp)
    const endTime = startTime + 86400 * 30 // ~4 weeks long
    const acc = SCHEDULE_ACCOUNTS[i % SCHEDULE_ACCOUNTS.length]
    const name = names[i % names.length]
    assignments.push({
      assignment_id: `demo-ass-${i + 1}`,
      title: name,
      start_timestamp: Math.floor(startTime),
      end_timestamp: Math.floor(endTime),
      resource: {
        resource_id: `res-ass-${i + 1}`,
        name: name,
        main_image: { url: getPlaceholderResourceImageUrl(name), __typename: 'Image' },
        location: DEMO_LOCATION,
        __typename: 'Resource'
      },
      location: DEMO_LOCATION,
      owner_account: {
        account_id: acc.account_id,
        name: acc.name,
        avatar: { url: getPlaceholderAvatarUrl(acc.name), __typename: 'Image' },
        __typename: 'Account'
      },
      __typename: 'ScheduleItem'
    })
  }

  assignments.sort((a, b) => a.start_timestamp - b.start_timestamp)

  return {
    schedule: {
      total: assignments.length,
      data: assignments,
      __typename: 'Schedule'
    }
  }
}

/**
 * Demo data for Assignments Ending Soon widget (schedule type: ASSIGNMENT)
 * @param {Object} options - { from_timestamp, to_timestamp }
 * @returns {Object} Demo schedule data with assignments ending in range
 */
export function getDemoAssignmentsEndingSoonData(options = {}) {
  const now = Math.floor(Date.now() / 1000)
  const { from_timestamp = now, to_timestamp = now + 7 * 86400 } = options

  const names = [
    'Private Office C', 'Studio 2', 'Dedicated Desk South',
    'Meeting Room East', 'Conference Room Alpha', 'Quiet Office'
  ]

  const assignments = []
  // Fixed count based on period: current (future) gets 2, previous (past) gets 2 → flat (0% change, no negative)
  const isFuturePeriod = to_timestamp > now
  const count = isFuturePeriod ? 2 : 2
  for (let i = 0; i < count; i++) {
    const endTime = from_timestamp + ((i + 1) / (count + 1)) * (to_timestamp - from_timestamp)
    const startTime = endTime - 86400 * 30 // Started ~4 weeks ago
    const acc = SCHEDULE_ACCOUNTS[(i + 3) % SCHEDULE_ACCOUNTS.length]
    const name = names[i % names.length]
    assignments.push({
      assignment_id: `demo-ase-${i + 1}`,
      title: name,
      start_timestamp: Math.floor(startTime),
      end_timestamp: Math.floor(endTime),
      resource: {
        resource_id: `res-ase-${i + 1}`,
        name: name,
        main_image: { url: getPlaceholderResourceImageUrl(name), __typename: 'Image' },
        location: DEMO_LOCATION,
        __typename: 'Resource'
      },
      location: DEMO_LOCATION,
      owner_account: {
        account_id: acc.account_id,
        name: acc.name,
        avatar: { url: getPlaceholderAvatarUrl(acc.name), __typename: 'Image' },
        __typename: 'Account'
      },
      __typename: 'ScheduleItem'
    })
  }

  assignments.sort((a, b) => a.end_timestamp - b.end_timestamp)

  return {
    schedule: {
      total: assignments.length,
      data: assignments,
      __typename: 'Schedule'
    }
  }
}

/**
 * Demo data for Top Bookers widget (schedule type: BOOKING, grouped by user)
 * @param {Object} options - { from_timestamp, to_timestamp }
 * @returns {Object} Demo schedule data with many bookings for grouping
 */
export function getDemoTopBookersData(options = {}) {
  const now = Math.floor(Date.now() / 1000)
  const { from_timestamp = now - 30 * 86400, to_timestamp = now } = options

  const timeRange = to_timestamp - from_timestamp
  // Top bookers get more bookings so the ranking is interesting
  const userBookings = [18, 14, 11, 8, 6, 4, 3, 2]
  const bookings = []

  for (let u = 0; u < userBookings.length; u++) {
    const acc = SCHEDULE_ACCOUNTS[u % SCHEDULE_ACCOUNTS.length]
    for (let j = 0; j < userBookings[u]; j++) {
      const startTime = from_timestamp + Math.random() * timeRange
      bookings.push({
        start_timestamp: Math.floor(startTime),
        owner_account: {
          account_id: acc.account_id,
          name: acc.name,
          __typename: 'Account'
        },
        __typename: 'ScheduleItem'
      })
    }
  }

  return {
    schedule: {
      total: bookings.length,
      data: bookings,
      __typename: 'Schedule'
    }
  }
}

/**
 * Demo data for Upcoming Tours widget (schedule type: TOUR)
 * @param {Object} options - { from_timestamp, to_timestamp }
 * @returns {Object} Demo schedule data with tours
 */
export function getDemoUpcomingToursData(options = {}) {
  const now = Math.floor(Date.now() / 1000)
  const { to_timestamp = now + 7 * 86400 } = options

  const tours = []
  // Fixed count based on period: current (future) gets 8, previous (past) gets 5 → positive comparison
  const isFuturePeriod = to_timestamp > now
  const count = isFuturePeriod ? 8 : 6
  for (let i = 0; i < count; i++) {
    const spread = isFuturePeriod ? (to_timestamp - now) : (to_timestamp - (options.from_timestamp || now - 7 * 86400))
    const base = isFuturePeriod ? now + 1800 : (options.from_timestamp || now - 7 * 86400)
    const startTime = base + ((i + 1) / (count + 1)) * spread
    const acc = SCHEDULE_ACCOUNTS[(i + 5) % SCHEDULE_ACCOUNTS.length]
    const title = `Property Tour ${i + 1}`
    tours.push({
      tour_id: `demo-tour-${i + 1}`,
      title: title,
      start_timestamp: Math.floor(startTime),
      end_timestamp: Math.floor(startTime + 1800),
      resource: {
        resource_id: `res-tour-${i + 1}`,
        name: `Tour Space ${i + 1}`,
        main_image: { url: getPlaceholderResourceImageUrl(`Tour Space ${i + 1}`), __typename: 'Image' },
        location: DEMO_LOCATION,
        __typename: 'Resource'
      },
      location: DEMO_LOCATION,
      owner_account: {
        account_id: acc.account_id,
        name: acc.name,
        avatar: { url: getPlaceholderAvatarUrl(acc.name), __typename: 'Image' },
        __typename: 'Account'
      },
      __typename: 'ScheduleItem'
    })
  }

  tours.sort((a, b) => a.start_timestamp - b.start_timestamp)

  return {
    schedule: {
      total: tours.length,
      data: tours,
      __typename: 'Schedule'
    }
  }
}

/**
 * Demo data for Completed Tours widget (schedule type: TOUR, status: ENDED)
 * @param {Object} options - { from_timestamp, to_timestamp }
 * @returns {Object} Demo schedule data with completed tours count
 */
export function getDemoCompletedToursData(options = {}) {
  const now = Math.floor(Date.now() / 1000)
  const { to_timestamp = now } = options
  // Past period gets 8, previous period gets 5 → positive comparison
  const isPastPeriod = to_timestamp <= now + 86400
  const count = isPastPeriod ? 8 : 5

  const tours = []
  const tourCount = Math.min(count, 3)
  for (let i = 0; i < tourCount; i++) {
    const spread = to_timestamp - (options.from_timestamp || now - 7 * 86400)
    const base = options.from_timestamp || now - 7 * 86400
    const startTime = base + ((i + 1) / (tourCount + 1)) * spread
    const acc = SCHEDULE_ACCOUNTS[(i + 3) % SCHEDULE_ACCOUNTS.length]
    const title = `Property Tour ${i + 1}`
    tours.push({
      tour_id: `demo-completed-tour-${i + 1}`,
      title: title,
      start_timestamp: Math.floor(startTime),
      end_timestamp: Math.floor(startTime + 1800),
      resource: {
        resource_id: `res-ctour-${i + 1}`,
        name: `Tour Space ${i + 1}`,
        main_image: { url: getPlaceholderResourceImageUrl(`Tour Space ${i + 1}`), __typename: 'Image' },
        location: DEMO_LOCATION,
        __typename: 'Resource'
      },
      location: DEMO_LOCATION,
      owner_account: {
        account_id: acc.account_id,
        name: acc.name,
        avatar: { url: getPlaceholderAvatarUrl(acc.name), __typename: 'Image' },
        __typename: 'Account'
      },
      __typename: 'ScheduleItem'
    })
  }

  tours.sort((a, b) => b.start_timestamp - a.start_timestamp)

  return {
    schedule: {
      total: count,
      data: tours,
      __typename: 'Schedule'
    }
  }
}

/**
 * Gets demo data for other widget types
 */
export function getDemoUsersData() {
  // Thriving 60-80 desk space: strong user growth
  return {
    users: [
      { id: '1', name: 'Alex Chen', email: 'alex.chen@acme.co', status: 'active' },
      { id: '2', name: 'Jordan Taylor', email: 'jordan.t@startup.io', status: 'active' },
      { id: '3', name: 'Quinn Wilson', email: 'quinn.w@design.co', status: 'active' },
      { id: '4', name: 'Sam Park', email: 'sam.p@tech.com', status: 'active' },
      { id: '5', name: 'Riley Brown', email: 'riley.b@consulting.com', status: 'active' }
    ],
    change: 12
  }
}

export function getDemoTimeSavedData(period = 'current') {
  // ~2.5 work days (20 hours = 1200 min) per 7 days; previous slightly less for positive comparison
  const current = [
    { workflowId: '1001', name: 'Automated Check-in', minutesSaved: 35 * 9, count: 9, isDefault: true },
    { workflowId: '1002', name: 'Self-Service Booking', minutesSaved: 45 * 7, count: 7, isDefault: false },
    { workflowId: '1003', name: 'Automated Reminders', minutesSaved: 25 * 6, count: 6, isDefault: true },
    { workflowId: '1004', name: 'Payment Processing', minutesSaved: 35 * 5, count: 5, isDefault: false },
    { workflowId: '1005', name: 'Document Signing', minutesSaved: 30 * 5, count: 5, isDefault: false }
  ]
  const previous = [
    { workflowId: '1001', name: 'Automated Check-in', minutesSaved: 35 * 7, count: 7, isDefault: true },
    { workflowId: '1002', name: 'Self-Service Booking', minutesSaved: 45 * 6, count: 6, isDefault: false },
    { workflowId: '1003', name: 'Automated Reminders', minutesSaved: 25 * 5, count: 5, isDefault: true },
    { workflowId: '1004', name: 'Payment Processing', minutesSaved: 35 * 4, count: 4, isDefault: false },
    { workflowId: '1005', name: 'Document Signing', minutesSaved: 30 * 4, count: 4, isDefault: false }
  ]
  return { enrollments: period === 'previous' ? previous : current }
}

export function getDemoRecentAutomationEnrollmentsData() {
  const now = Math.floor(Date.now() / 1000) // Current time in Unix seconds
  const workflows = [
    'Automated Check-in',
    'Self-Service Booking',
    'Automated Reminders',
    'Payment Processing',
    'Document Signing',
    'Welcome Email',
    'Checkout Reminder',
    'Feedback Collection'
  ]
  const accounts = [
    'Alex Chen', 'Jordan Taylor', 'Quinn Wilson', 'Sam Park', 'Riley Brown',
    'Casey Rivera', 'Morgan Kim', 'Avery Lee', 'Taylor Patel', 'Jamie O\'Brien'
  ]
  // Fixed offsets: 8 today, 5 yesterday, 5 older – so current > previous (positive comparison)
  const offsets = [
    30 * 60, 60 * 60, 90 * 60, 2 * 3600, 2.5 * 3600, 3 * 3600, 3.5 * 3600, 4 * 3600, // 8 today
    1 * 86400 + 3600, 1 * 86400 + 7200, 1.5 * 86400, 1.8 * 86400, 2 * 86400, // 5 yesterday
    3 * 86400, 4 * 86400, 5 * 86400, 5.5 * 86400, 6 * 86400 // 5 in last 3–7 days
  ]

  const enrollments = offsets.map((offsetSec, i) => {
    const timestamp = now - offsetSec
    const accountName = accounts[i % accounts.length]
    const workflowIndex = i % workflows.length
    const workflowId = 10000 + workflowIndex
    return {
      workflow_enrollment_id: (1000000 + i).toString(),
      status: 'completed',
      workflow: {
        workflow_id: workflowId.toString(),
        name: workflows[workflowIndex]
      },
      account: {
        name: accountName,
        avatar: {
          url: getPlaceholderAvatarUrl(accountName)
        }
      },
      created_timestamp: Math.floor(timestamp)
    }
  })

  // Sort by created_timestamp descending (most recent first)
  enrollments.sort((a, b) => b.created_timestamp - a.created_timestamp)

  return {
    workflowEnrollments: {
      total: enrollments.length,
      data: enrollments
    }
  }
}

/**
 * Demo data for custom automations count - 14 automated workflows
 */
export function getDemoCustomAutomationsData() {
  return {
    workflows: { total: 18, __typename: 'WorkflowConnection' }
  }
}

/**
 * Demo data for actions this period - 6-month flex space: 2,847 actions
 */
export function getDemoActionsThisPeriodData() {
  return {
    workflowActionsCount: {
      total: 3240,
      chargeable: 2460,
      data: [
        { workflow: { workflow_id: '1', name: 'Automated Check-in' }, total: 1024, chargeable: 1024 },
        { workflow: { workflow_id: '2', name: 'Self-Service Booking' }, total: 756, chargeable: 756 },
        { workflow: { workflow_id: '3', name: 'Automated Reminders' }, total: 612, chargeable: 368 },
        { workflow: { workflow_id: '4', name: 'Payment Processing' }, total: 498, chargeable: 312 },
        { workflow: { workflow_id: '5', name: 'Document Signing' }, total: 350, chargeable: 0 }
      ],
      __typename: 'WorkflowActionsCount'
    }
  }
}

/**
 * Demo data for check-ins - close comparison, positive change
 * @param {string} period - 'current' (47) or 'previous' (45)
 */
export function getDemoCheckInsData(period = 'current') {
  const total = period === 'previous' ? 52 : 61
  return { checkins: { total, __typename: 'Checkins' } }
}

/**
 * Demo data for account plans - positive comparisons
 * Plans Ending Soon: current 2, previous 3 (fewer ending = green with inverted chip)
 * Plans Starting Soon: current 6, previous 4 (more starting = green)
 */
export function getDemoAccountPlansData(period = 'current') {
  const now = Math.floor(Date.now() / 1000)
  const currentPlans = [
    { name: 'Day Pass', start: now - 86400 * 5, end: now + 86400 * 3, payer: 'Casey Rivera' },
    { name: 'Weekend Desk', start: now - 86400 * 2, end: now + 86400 * 28, payer: 'Morgan Kim' },
    { name: 'Flex Desk', start: now + 86400 * 2, end: now + 86400 * 32, payer: 'Alex Chen' },
    { name: 'Hot Desk', start: now + 86400 * 5, end: now + 86400 * 12, payer: 'Quinn Wilson' },
    { name: 'Dedicated Desk', start: now + 86400 * 1, end: now + 86400 * 31, payer: 'Jordan Taylor' },
    { name: 'Studio Suite', start: now + 86400 * 4, end: now + 86400 * 34, payer: 'Sam Park' }
  ]
  const previousPlans = [
    { name: 'Starter Desk', start: now - 86400 * 20, end: now - 86400 * 10, payer: 'Avery Lee' },
    { name: 'Past Flex', start: now - 86400 * 25, end: now - 86400 * 18, payer: 'Taylor Patel' },
    { name: 'Basic Desk', start: now - 86400 * 30, end: now - 86400 * 22, payer: 'Jamie O\'Brien' },
    { name: 'Weekly Pass', start: now - 86400 * 18, end: now - 86400 * 12, payer: 'Skyler Brooks' }
  ]
  const plans = period === 'previous' ? previousPlans : currentPlans
  return {
    accountPlans: {
      total: plans.length,
      found_without_filters: plans.length,
      data: plans.map((p, i) => ({
        account_plan_id: `demo-plan-${i + 1}`,
        plan_template: { image: { url: null } },
        payer_account: { account_id: `acc-${i + 1}`, name: p.payer, type: 'user', avatar: { url: getPlaceholderAvatarUrl(p.payer) } },
        name: p.name,
        status: 'active',
        start_timestamp: p.start,
        end_timestamp: p.end
      })),
      __typename: 'AccountPlanConnection'
    }
  }
}

/**
 * Demo data for dashboard KPIs (conversion rates, converted leads)
 * @param {string} period - 'current' or 'previous' (previous returns slightly lower values for positive comparisons)
 */
export function getDemoDashboardKPIsData(period = 'current') {
  const accounts = [
    { account_id: '1', name: 'Alex Chen', email: 'alex@acme.co', avatar: { url: getPlaceholderAvatarUrl('Alex Chen') } },
    { account_id: '2', name: 'Jordan Taylor', email: 'jordan@startup.io', avatar: { url: getPlaceholderAvatarUrl('Jordan Taylor') } },
    { account_id: '3', name: 'Quinn Wilson', email: 'quinn@design.co', avatar: { url: getPlaceholderAvatarUrl('Quinn Wilson') } },
    { account_id: '4', name: 'Sam Park', email: 'sam@tech.com', avatar: { url: getPlaceholderAvatarUrl('Sam Park') } },
    { account_id: '5', name: 'Riley Brown', email: 'riley@consulting.com', avatar: { url: getPlaceholderAvatarUrl('Riley Brown') } }
  ]
  const isPrevious = period === 'previous'
  return {
    dashboardKPIs: {
      kpis: [
        { name: 'tour_conversion_rate', value: isPrevious ? '33.4' : '36.8' },
        { name: 'inquiry_conversion_rate', value: isPrevious ? '27.8' : '31.2' },
        { name: 'dropin_conversion_rate', value: isPrevious ? '38.9' : '43.5' }
      ],
      automated_inquiry_converted_accounts: { total: isPrevious ? 12 : 16, data: accounts.slice(0, 3) },
      automated_tour_converted_accounts: { total: isPrevious ? 18 : 22, data: accounts },
      automated_dropin_converted_accounts: { total: isPrevious ? 8 : 12, data: accounts.slice(0, 2) },
      inquiry_converted_accounts: { total: isPrevious ? 24 : 28, data: accounts },
      dropin_converted_accounts: { total: isPrevious ? 16 : 18, data: accounts.slice(0, 4) },
      tour_converted_accounts: { total: isPrevious ? 21 : 24, data: accounts },
      tour_bought_pass_or_plan_accounts: { total: isPrevious ? 9 : 11, data: accounts.slice(0, 3) },
      dropin_made_second_booking_accounts: { total: isPrevious ? 11 : 14, data: accounts.slice(0, 4) },
      dropin_bought_pass_or_plan_accounts: { total: isPrevious ? 8 : 9, data: accounts.slice(0, 2) },
      __typename: 'DashboardKPIs'
    }
  }
}

/**
 * Demo data for invoices by status - 6-month flex space
 * Shared source of truth for all invoice-related widgets
 */
const DEMO_INVOICE_STATUSES = [
  { name: 'Paid', amount: 38140 },
  { name: 'Upcoming', amount: 14350 },
  { name: 'Due', amount: 1200 },
  { name: 'Overdue', amount: 0 },
  { name: 'Void', amount: 0 },
  { name: 'Processing', amount: 2415 }
]

export function getDemoInvoicesByStatusData() {
  return {
    invoicesByStatus: {
      statuses: DEMO_INVOICE_STATUSES,
      currency: 'USD',
      currency_symbol: '$',
      locale: 'en-US'
    }
  }
}

/**
 * Demo data for Invoices Paid widget - close comparison, positive change
 */
export function getDemoInvoicesPaidData(period = 'current') {
  const paid = period === 'previous' ? 34680 : 38140
  return { invoicesPaidTotal: paid }
}

/**
 * Demo data for Revenue collected widget - payments received in the period,
 * with a per-account breakdown for the detailed view / view-all panel.
 */
export function getDemoRevenueCollectedData(period = 'current') {
  const accountsBase = [
    { account_id: '1001', name: 'Acme Studios', revenue: 9820 },
    { account_id: '1002', name: 'Nova Collective', revenue: 7450 },
    { account_id: '1003', name: 'Bright Labs', revenue: 6180 },
    { account_id: '1004', name: 'Harbor Coworking', revenue: 4990 },
    { account_id: '1005', name: 'Maple & Co', revenue: 3870 },
    { account_id: '1006', name: 'Riverside Ventures', revenue: 2540 },
    { account_id: '1007', name: 'Summit Partners', revenue: 1890 }
  ]
  const factor = period === 'previous' ? 0.9 : 1
  const accounts = accountsBase
    .map((a) => ({
      ...a,
      revenue: Math.round(a.revenue * factor),
      avatar: getPlaceholderAvatarUrl(a.account_id)
    }))
    .sort((a, b) => b.revenue - a.revenue)
  const revenueCollectedTotal = accounts.reduce((sum, a) => sum + a.revenue, 0)
  return { revenueCollectedTotal, accounts }
}

/**
 * Demo data for Unpaid Invoices widget - matches Due + Overdue from Invoices widget
 */
export function getDemoUnpaidInvoicesData() {
  const due = DEMO_INVOICE_STATUSES.find(s => s.name === 'Due')?.amount ?? 3200
  const overdue = DEMO_INVOICE_STATUSES.find(s => s.name === 'Overdue')?.amount ?? 850
  const unpaid = due + overdue
  return {
    unpaidInvoicesTotal: unpaid,
    currency: 'USD',
    currency_symbol: '$',
    locale: 'en-US'
  }
}

/**
 * Demo data for Credit Owed widget - negative balance due/overdue invoices
 */
export function getDemoCreditOwedData() {
  const creditAmount = 0
  return {
    creditOwedTotal: creditAmount,
    currency: 'USD',
    currency_symbol: '$',
    locale: 'en-US'
  }
}

/**
 * Demo data for top accounts by revenue - 6-month flex space
 * Row format: [name, revenue, avatar, account_id]
 */
export function getDemoTopAccountsByRevenueData() {
  const accounts = [
    { name: 'Acme Corp', revenue: 11240, avatar: getPlaceholderAvatarUrl('Acme Corp'), id: 'acc-1' },
    { name: 'TechStart Inc', revenue: 8370, avatar: getPlaceholderAvatarUrl('TechStart Inc'), id: 'acc-2' },
    { name: 'Design Studio', revenue: 6510, avatar: getPlaceholderAvatarUrl('Design Studio'), id: 'acc-3' },
    { name: 'Consulting Co', revenue: 5680, avatar: getPlaceholderAvatarUrl('Consulting Co'), id: 'acc-4' },
    { name: 'Innovation Labs', revenue: 4790, avatar: getPlaceholderAvatarUrl('Innovation Labs'), id: 'acc-5' }
  ]
  return {
    analyticsQuery: {
      currency: 'USD',
      currency_symbol: '$',
      locale: 'en-US',
      data: accounts.map(a => [a.name, a.revenue, a.avatar, a.id]),
      total: accounts.length
    }
  }
}

/**
 * Demo data for currently checked in accounts - 6-month flex space
 */
export function getDemoCheckedInAccountsData() {
  const now = Math.floor(Date.now() / 1000)
  const accounts = [
    {
      account_id: '1',
      name: 'Alex Chen',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-1',
        start_timestamp: now - 3600,
        end_timestamp: null,
        created_timestamp: now - 3600,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Alex Chen') }
    },
    {
      account_id: '2',
      name: 'Jordan Taylor',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-2',
        start_timestamp: now - 7200,
        end_timestamp: null,
        created_timestamp: now - 7200,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Jordan Taylor') }
    },
    {
      account_id: '3',
      name: 'Quinn Wilson',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-3',
        start_timestamp: now - 1800,
        end_timestamp: null,
        created_timestamp: now - 1800,
        location: { location_id: 'demo-location-2', name: 'Main Floor' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Quinn Wilson') }
    },
    {
      account_id: '4',
      name: 'Sam Park',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-4',
        start_timestamp: now - 5400,
        end_timestamp: null,
        created_timestamp: now - 5400,
        location: { location_id: 'demo-location-2', name: 'Main Floor' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Sam Park') }
    },
    {
      account_id: '5',
      name: 'Riley Brown',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-5',
        start_timestamp: now - 900,
        end_timestamp: null,
        created_timestamp: now - 900,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Riley Brown') }
    },
    {
      account_id: '6',
      name: 'Morgan Lee',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-6',
        start_timestamp: now - 2700,
        end_timestamp: null,
        created_timestamp: now - 2700,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Morgan Lee') }
    },
    {
      account_id: '7',
      name: 'Casey Davis',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-7',
        start_timestamp: now - 4500,
        end_timestamp: null,
        created_timestamp: now - 4500,
        location: { location_id: 'demo-location-2', name: 'Main Floor' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Casey Davis') }
    },
    {
      account_id: '8',
      name: 'Jamie Martinez',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-8',
        start_timestamp: now - 1200,
        end_timestamp: null,
        created_timestamp: now - 1200,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Jamie Martinez') }
    },
    {
      account_id: '9',
      name: 'Taylor Kim',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-9',
        start_timestamp: now - 6300,
        end_timestamp: null,
        created_timestamp: now - 6300,
        location: { location_id: 'demo-location-2', name: 'Main Floor' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Taylor Kim') }
    },
    {
      account_id: '10',
      name: 'Jordan Smith',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-10',
        start_timestamp: now - 300,
        end_timestamp: null,
        created_timestamp: now - 300,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Jordan Smith') }
    },
    {
      account_id: '11',
      name: 'Avery Johnson',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-11',
        start_timestamp: now - 3900,
        end_timestamp: null,
        created_timestamp: now - 3900,
        location: { location_id: 'demo-location-2', name: 'Main Floor' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Avery Johnson') }
    },
    {
      account_id: '12',
      name: 'Riley Thompson',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-12',
        start_timestamp: now - 2100,
        end_timestamp: null,
        created_timestamp: now - 2100,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Riley Thompson') }
    },
    {
      account_id: '13',
      name: 'Drew Nguyen',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-13',
        start_timestamp: now - 4800,
        end_timestamp: null,
        created_timestamp: now - 4800,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Drew Nguyen') }
    },
    {
      account_id: '14',
      name: 'Sage Patel',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-14',
        start_timestamp: now - 1500,
        end_timestamp: null,
        created_timestamp: now - 1500,
        location: { location_id: 'demo-location-2', name: 'Main Floor' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Sage Patel') }
    },
    {
      account_id: '15',
      name: 'Blake Torres',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-15',
        start_timestamp: now - 5100,
        end_timestamp: null,
        created_timestamp: now - 5100,
        location: { location_id: 'demo-location-2', name: 'Main Floor' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Blake Torres') }
    },
    {
      account_id: '16',
      name: 'Rowan Ellis',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-16',
        start_timestamp: now - 3300,
        end_timestamp: null,
        created_timestamp: now - 3300,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Rowan Ellis') }
    },
    {
      account_id: '17',
      name: 'Emery Walsh',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-17',
        start_timestamp: now - 600,
        end_timestamp: null,
        created_timestamp: now - 600,
        location: { location_id: 'demo-location-1', name: 'Innovation Hub' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Emery Walsh') }
    },
    {
      account_id: '18',
      name: 'Harley Soto',
      is_checked_in: true,
      latest_checkin: {
        checkin_id: 'ci-18',
        start_timestamp: now - 4200,
        end_timestamp: null,
        created_timestamp: now - 4200,
        location: { location_id: 'demo-location-2', name: 'Main Floor' }
      },
      avatar: { url: getPlaceholderAvatarUrl('Harley Soto') }
    }
  ]
  return {
    accounts: {
      total: 18,
      data: accounts,
      __typename: 'AccountConnection'
    }
  }
}

/**
 * Demo data for tasks - 6-month flex space
 */
export function getDemoTasksData() {
  const now = Math.floor(Date.now() / 1000)
  const tasks = [
    { task_id: 'task-1', name: 'Review monthly reports', assignee: { user_id: '1', fullname: 'Demo User' }, status: 'TO_DO', due_timestamp: now + 86400, is_due: false },
    { task_id: 'task-2', name: 'Follow up with new leads', assignee: { user_id: '1', fullname: 'Demo User' }, status: 'TO_DO', due_timestamp: now + 86400 * 2, is_due: false },
    { task_id: 'task-3', name: 'Update user onboarding docs', assignee: { user_id: '1', fullname: 'Demo User' }, status: 'TO_DO', due_timestamp: now - 86400, is_due: true },
    { task_id: 'task-4', name: 'Schedule team sync', assignee: { user_id: '1', fullname: 'Demo User' }, status: 'TO_DO', due_timestamp: now + 86400 * 5, is_due: false }
  ]
  return {
    tasks: {
      total: 4,
      found_without_filters: 4,
      data: tasks,
      __typename: 'TaskConnection'
    }
  }
}

/**
 * Demo data for latest messages / conversations - 6-month flex space
 */
export function getDemoConversationsData() {
  const now = Math.floor(Date.now() / 1000)
  const conversations = [
    {
      conversation_id: 'demo-conv-1',
      name: 'Alex Chen',
      latest_message: {
        user_id: '1',
        message: 'Thanks for the update on the meeting room!',
        plaintext: 'Thanks for the update on the meeting room!',
        timestamp: now - 3600
      },
      image: { url: getPlaceholderAvatarUrl('Alex Chen') },
      unread_message_count: 2,
      conversation_type: 'DIRECT',
      created_timestamp: now - 86400 * 3,
      users: []
    },
    {
      conversation_id: 'demo-conv-2',
      name: 'Jordan Taylor, Quinn Wilson',
      latest_message: {
        user_id: '2',
        message: 'Can we reschedule the tour to next week?',
        plaintext: 'Can we reschedule the tour to next week?',
        timestamp: now - 7200
      },
      image: { url: getPlaceholderAvatarUrl('Jordan Taylor') },
      unread_message_count: 1,
      conversation_type: 'GROUP',
      created_timestamp: now - 86400 * 5,
      users: []
    },
    {
      conversation_id: 'demo-conv-3',
      name: 'Sam Park',
      latest_message: {
        user_id: '4',
        message: 'Sent from a manual entry',
        plaintext: 'Sent from a manual entry',
        timestamp: now - 86400
      },
      image: { url: null },
      unread_message_count: 0,
      conversation_type: 'DIRECT',
      created_timestamp: now - 86400 * 7,
      users: []
    }
  ]
  return {
    conversations: {
      total: 3,
      unread_conversation_count: 2,
      unread_message_count: 3,
      conversation_sequence_up_to: null,
      has_more: false,
      data: conversations
    }
  }
}

/**
 * Demo data for plan revenue - close comparison, positive change
 * Row format: [planName, accountName, periodRevenue, planId, startTimestamp, endTimestamp, fullPrice]
 */
/**
 * Demo data for public group conversations - community groups
 */
export function getDemoPublicGroupConversationsData() {
  return {
    conversations: {
      total: 8,
      data: [
        {
          conversation_id: 'demo-pg-1',
          name: 'Community Announcements',
          message_count: 247,
          image: { url: '/placeholders/people/group-01.jpg' },
          conversation_type: 'PUBLIC_GROUP',
          users: ['demo-user'],
          joined: true
        },
        {
          conversation_id: 'demo-pg-2',
          name: 'Networking & Events',
          message_count: 183,
          image: { url: '/placeholders/people/group-02.jpg' },
          conversation_type: 'PUBLIC_GROUP',
          users: ['demo-user'],
          joined: true
        },
        {
          conversation_id: 'demo-pg-3',
          name: 'Freelancers Hub',
          message_count: 156,
          image: { url: '/placeholders/people/group-03.jpg' },
          conversation_type: 'PUBLIC_GROUP',
          users: ['demo-user'],
          joined: true
        },
        {
          conversation_id: 'demo-pg-4',
          name: 'Tech Talk',
          message_count: 134,
          image: { url: null },
          conversation_type: 'PUBLIC_GROUP',
          users: [],
          joined: false
        },
        {
          conversation_id: 'demo-pg-5',
          name: 'Lunch & Coffee',
          message_count: 98,
          image: { url: null },
          conversation_type: 'PUBLIC_GROUP',
          users: [],
          joined: false
        },
        {
          conversation_id: 'demo-pg-6',
          name: 'Lost & Found',
          message_count: 42,
          image: { url: null },
          conversation_type: 'PUBLIC_GROUP',
          users: ['demo-user'],
          joined: true
        },
        {
          conversation_id: 'demo-pg-7',
          name: 'Book Club',
          message_count: 31,
          image: { url: null },
          conversation_type: 'PUBLIC_GROUP',
          users: [],
          joined: false
        },
        {
          conversation_id: 'demo-pg-8',
          name: 'Fitness & Wellness',
          message_count: 19,
          image: { url: null },
          conversation_type: 'PUBLIC_GROUP',
          users: [],
          joined: false
        }
      ]
    }
  }
}

/**
 * Demo data for support chat conversations - user to admin messages
 */
export function getDemoSupportChatData() {
  return {
    conversations: {
      total: 12,
      data: [
        {
          conversation_id: 'demo-sc-1',
          name: 'Alex Chen & Innovation Hub Team',
          message_count: 34,
          latest_message: { plaintext: 'Hey, can I get access to the meeting room on Friday afternoon?', timestamp: 1741600000 },
          image: { url: getPlaceholderAvatarUrl('Alex Chen') },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-2',
          name: 'Jordan Taylor & Innovation Hub Team',
          message_count: 28,
          latest_message: { plaintext: 'Thanks for sorting out the Wi-Fi issue so quickly!', timestamp: 1741550000 },
          image: { url: getPlaceholderAvatarUrl('Jordan Taylor') },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-3',
          name: 'Quinn Wilson & Innovation Hub Team',
          message_count: 19,
          latest_message: { plaintext: 'Is there a way to book the podcast studio for next week?', timestamp: 1741500000 },
          image: { url: getPlaceholderAvatarUrl('Quinn Wilson') },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-4',
          name: 'Sam Park & Innovation Hub Team',
          message_count: 15,
          latest_message: { plaintext: 'The printer on the 2nd floor is jammed again.', timestamp: 1741450000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-5',
          name: 'Riley Brown & Innovation Hub Team',
          message_count: 11,
          latest_message: { plaintext: 'Do you have any availability for a dedicated desk next month?', timestamp: 1741400000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-6',
          name: 'Casey Rivera & Innovation Hub Team',
          message_count: 8,
          latest_message: { plaintext: 'Love the new coffee machine!', timestamp: 1741350000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-7',
          name: 'Morgan Kim & Innovation Hub Team',
          message_count: 6,
          latest_message: { plaintext: 'Can I bring a guest to the networking event?', timestamp: 1741300000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-8',
          name: 'Avery Lee & Innovation Hub Team',
          message_count: 4,
          latest_message: { plaintext: 'The AC in Studio 2 seems to be off.', timestamp: 1741250000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-9',
          name: 'Taylor Patel & Innovation Hub Team',
          message_count: 3,
          latest_message: { plaintext: 'How do I connect to the guest Wi-Fi?', timestamp: 1741200000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-10',
          name: 'Jamie O\'Brien & Innovation Hub Team',
          message_count: 2,
          latest_message: { plaintext: 'Thanks for the tour yesterday, signing up today!', timestamp: 1741150000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-11',
          name: 'Skyler Brooks & Innovation Hub Team',
          message_count: 1,
          latest_message: { plaintext: 'Hi, just moved in — where do I find the kitchen?', timestamp: 1741100000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        },
        {
          conversation_id: 'demo-sc-12',
          name: 'Parker Reed & Innovation Hub Team',
          message_count: 1,
          latest_message: { plaintext: 'Is there parking available for members?', timestamp: 1741050000 },
          image: { url: null },
          conversation_type: 'USER_TO_ADMINS',
          users: []
        }
      ]
    }
  }
}

/**
 * Demo data for discover groups panel - public groups not yet joined
 */
export function getDemoDiscoverGroupsData() {
  return {
    conversations: {
      total: 5,
      data: [
        {
          conversation_id: 'demo-dg-1',
          name: 'Startup Founders',
          message_count: 89,
          latest_message: { plaintext: 'Anyone attending the pitch night next Thursday?', timestamp: 1741600000 },
          image: { url: '/placeholders/people/group-02.jpg' },
          conversation_type: 'PUBLIC_GROUP',
          users: []
        },
        {
          conversation_id: 'demo-dg-2',
          name: 'Design & Creativity',
          message_count: 67,
          latest_message: { plaintext: 'Just shared some mockups in the shared drive — feedback welcome!', timestamp: 1741550000 },
          image: { url: null },
          conversation_type: 'PUBLIC_GROUP',
          users: []
        },
        {
          conversation_id: 'demo-dg-3',
          name: 'Remote Workers',
          message_count: 54,
          latest_message: { plaintext: 'Coffee run at 2pm if anyone wants to join', timestamp: 1741500000 },
          image: { url: null },
          conversation_type: 'PUBLIC_GROUP',
          users: []
        },
        {
          conversation_id: 'demo-dg-4',
          name: 'Pet Lovers',
          message_count: 23,
          latest_message: { plaintext: 'Bring your dogs to the patio this Friday!', timestamp: 1741450000 },
          image: { url: null },
          conversation_type: 'PUBLIC_GROUP',
          users: []
        },
        {
          conversation_id: 'demo-dg-5',
          name: 'Weekend Sports',
          message_count: 15,
          latest_message: { plaintext: 'Basketball game Saturday at 10am — need 2 more players', timestamp: 1741400000 },
          image: { url: '/placeholders/people/group-01.jpg' },
          conversation_type: 'PUBLIC_GROUP',
          users: []
        }
      ]
    }
  }
}

export function getDemoBestPracticesData() {
  return [
    { type: 'TOUR_REMINDER', priority: 6, is_covered: true, proof_count: 24, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-1', name: 'Tour reminder' }, covering_workflow_recipe: null },
    { type: 'TOUR_FOLLOW_UP', priority: 8, is_covered: true, proof_count: 36, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-2', name: 'Tour follow-up' }, covering_workflow_recipe: null },
    { type: 'INACTIVITY_FOLLOW_UP', priority: 3, is_covered: true, proof_count: 18, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-8', name: 'Re-engage inactive users' }, covering_workflow_recipe: null },
    { type: 'OPERATIONAL_TASKS', priority: 4, is_covered: true, proof_count: 28, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-3', name: 'Auto-create tasks' }, covering_workflow_recipe: null },
    { type: 'REPEAT_BOOKER_FEEDBACK', priority: 6, is_covered: true, proof_count: 15, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-9', name: 'Repeat booker feedback' }, covering_workflow_recipe: null },
    { type: 'ASSIGNMENT_ONBOARDING', priority: 8, is_covered: true, proof_count: 9, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-10', name: 'Assignment onboarding' }, covering_workflow_recipe: null },
    { type: 'ASSIGNMENT_RENEWAL', priority: 9, is_covered: true, proof_count: 6, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-11', name: 'Renewal outreach' }, covering_workflow_recipe: null },
    { type: 'ROUTINE_COMMUNICATION', priority: 4, is_covered: true, proof_count: 156, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-4', name: 'Automated messages' }, covering_workflow_recipe: null },
    { type: 'INVOICE_REMINDER', priority: 9, is_covered: true, proof_count: 89, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-5', name: 'Invoice reminder' }, covering_workflow_recipe: null },
    { type: 'INVOICE_OVERDUE_FOLLOW_UP', priority: 9, is_covered: true, proof_count: 12, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-6', name: 'Overdue follow-up' }, covering_workflow_recipe: null },
    { type: 'INQUIRY_FOLLOW_UP', priority: 8, is_covered: true, proof_count: 67, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-7', name: 'Inquiry follow-up' }, covering_workflow_recipe: null },
    { type: 'DROP_IN_FOLLOW_UP', priority: 8, is_covered: true, proof_count: 11, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-12', name: 'Drop-in follow-up' }, covering_workflow_recipe: null },
    { type: 'PLAN_ONBOARDING', priority: 5, is_covered: true, proof_count: 6, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-13', name: 'Welcome new members' }, covering_workflow_recipe: null },
    { type: 'PLAN_RENEWAL', priority: 7, is_covered: true, proof_count: 8, missed_opportunity: false, opportunity_count: 0, covering_workflow: { workflow_id: 'demo-14', name: 'Plan renewal outreach' }, covering_workflow_recipe: null }
  ]
}

export function getDemoPlanRevenueData(period = 'current') {
  const now = Math.floor(Date.now() / 1000)
  const currentPlans = [
    ['Flex Desk Monthly', 'Alex Chen', 549, 'plan-1', now - 86400 * 15, now + 86400 * 15, 549],
    ['Private Office', 'Jordan Taylor', 1475, 'plan-2', now - 86400 * 60, now + 86400 * 35, 1475],
    ['Hot Desk Weekly', 'Quinn Wilson', 215, 'plan-3', now - 86400 * 5, now + 86400 * 2, 215],
    ['Meeting Room Pass', 'Sam Park', 385, 'plan-4', now - 86400 * 20, now + 86400 * 10, 385],
    ['Team Suite', 'Riley Brown', 2590, 'plan-5', now - 86400 * 45, now + 86400 * 59, 2590]
  ]
  const previousPlans = [
    ['Flex Desk Monthly', 'Alex Chen', 499, 'plan-1', now - 86400 * 45, now + 86400 * 15, 549],
    ['Private Office', 'Jordan Taylor', 1380, 'plan-2', now - 86400 * 90, now + 86400 * 35, 1475],
    ['Hot Desk Weekly', 'Quinn Wilson', 195, 'plan-3', now - 86400 * 12, now + 86400 * 2, 215],
    ['Meeting Room Pass', 'Sam Park', 345, 'plan-4', now - 86400 * 27, now + 86400 * 10, 385],
    ['Team Suite', 'Riley Brown', 2410, 'plan-5', now - 86400 * 75, now + 86400 * 59, 2590]
  ]
  const plans = period === 'previous' ? previousPlans : currentPlans
  const total = plans.reduce((sum, row) => sum + (row[2] || 0), 0)
  return {
    analyticsQuery: {
      currency: 'USD',
      currency_symbol: '$',
      locale: 'en-US',
      total,
      data: plans,
      __typename: 'AnalyticsQuery'
    }
  }
}
